/**
 * W5 — the compact multi-bay column (mockup panel 2): Hands-On Now / Unattended / Waiting in bay / Next in Queue.
 *
 * Rendered by TechnicianColumn ONLY when deriveMultiBayActivity() is active (≥2 bays live, one hands-on or
 * unattended, shop-local today). It is a presentation GROUPING over the column's own ordered list:
 *   - every ticket is rendered exactly once, through the column's own renderTicketRow (the real TicketCard with
 *     all its actions, timers, kebab and drag behaviour — no parallel card variant, no duplicated sortable id);
 *   - every row keeps its ORIGINAL column index (the ghost preview and cascade shift are index-based);
 *   - section headings state facts from server rows; nothing is shown as "unattended" without an open operation.
 */
import type { ReactNode } from 'react';
import { Wrench, Cog, PauseCircle, ListOrdered } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  partitionMultiBaySections, unattendedExpectedEnd, type MultiBayActivity, type LiveBayVehicle,
} from '@/lib/dayboard/multiBayActivity';
import { unattendedKindLabel } from '@/lib/timer/workSwitch';
import { UnattendedFlagBadges } from './UnattendedFlagBadges';

export interface MultiBayTechnicianSectionsProps<T extends { id: string }> {
  technicianId: string;
  techTickets: readonly T[];
  activity: MultiBayActivity;
  renderTicketRow: (ticket: T, originalIdx: number) => ReactNode;
  /** Bay display name (falls back to "Bay"). */
  bayNameOf?: (bayId: string) => string | null;
  /** Shop-local clock formatter (the column's formatTimeInShopTZ). */
  formatTime: (d: Date) => string;
}

function BayChip({ bayId, bayNameOf }: { bayId: string; bayNameOf?: (id: string) => string | null }) {
  return <Badge variant="secondary" className="text-[10px]">{bayNameOf?.(bayId) ?? 'Bay'}</Badge>;
}

function UnattendedFacts({ v, formatTime }: { v: LiveBayVehicle; formatTime: (d: Date) => string }) {
  return (
    <ul className="mb-1 space-y-0.5 text-[11px] text-muted-foreground">
      {v.unattended.map((op) => {
        const end = unattendedExpectedEnd(op);
        return (
          <li key={op.id} data-testid={`multibay-op-${op.id}`}>
            <span className="font-medium text-foreground">{unattendedKindLabel(op.kind)}</span>
            {' · since '}{formatTime(new Date(op.started_at))}
            {' · '}{end ? `expected end ${formatTime(end)} (stated)` : 'until ended'}
            {' · no labor'}
            {op.flags && op.flags.length > 0 && (
              <span className="ml-1 inline-flex flex-wrap gap-1 align-middle"><UnattendedFlagBadges flags={op.flags} /></span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function MultiBayTechnicianSections<T extends { id: string }>({
  technicianId, techTickets, activity, renderTicketRow, bayNameOf, formatTime,
}: MultiBayTechnicianSectionsProps<T>) {
  const s = partitionMultiBaySections(techTickets, activity);
  const hid = (k: string) => `multibay-${technicianId}-${k}`;
  return (
    <div data-testid={`multibay-sections-${technicianId}`} className="space-y-3">
      <section aria-labelledby={hid('handson')} data-testid="multibay-section-hands-on">
        <h4 id={hid('handson')} className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
          <Wrench className="h-3.5 w-3.5" aria-hidden />Hands-On Now
          {s.handsOn.map(({ vehicle }) => <BayChip key={vehicle.ticketId} bayId={vehicle.bayId} bayNameOf={bayNameOf} />)}
        </h4>
        {s.handsOn.length === 0 && <p className="text-[11px] text-muted-foreground">No hands-on work running.</p>}
        {s.handsOn.map(({ ticket, idx, vehicle }) => (
          <div key={ticket.id}>
            {vehicle.unattended.length > 0 && <UnattendedFacts v={vehicle} formatTime={formatTime} />}
            {renderTicketRow(ticket, idx)}
          </div>
        ))}
      </section>

      {s.unattended.length > 0 && (
        <section aria-labelledby={hid('unattended')} data-testid="multibay-section-unattended">
          <h4 id={hid('unattended')} className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
            <Cog className="h-3.5 w-3.5" aria-hidden />Unattended
          </h4>
          {s.unattended.map(({ ticket, idx, vehicle }) => (
            <div key={ticket.id}>
              <div className="mb-0.5 flex items-center gap-1"><BayChip bayId={vehicle.bayId} bayNameOf={bayNameOf} /></div>
              <UnattendedFacts v={vehicle} formatTime={formatTime} />
              {renderTicketRow(ticket, idx)}
            </div>
          ))}
        </section>
      )}

      {s.pausedInBay.length > 0 && (
        <section aria-labelledby={hid('waiting')} data-testid="multibay-section-waiting">
          <h4 id={hid('waiting')} className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
            <PauseCircle className="h-3.5 w-3.5" aria-hidden />Waiting in bay
          </h4>
          {s.pausedInBay.map(({ ticket, idx, vehicle }) => (
            <div key={ticket.id}>
              <div className="mb-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                <BayChip bayId={vehicle.bayId} bayNameOf={bayNameOf} />
                {vehicle.state === 'switched_away' ? 'Switched away — waiting for the technician' : `Paused in bay · ${vehicle.holdReason ?? 'no reason recorded'}`}
              </div>
              {renderTicketRow(ticket, idx)}
            </div>
          ))}
        </section>
      )}

      <section aria-labelledby={hid('queue')} data-testid="multibay-section-queue">
        <h4 id={hid('queue')} className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
          <ListOrdered className="h-3.5 w-3.5" aria-hidden />Next in Queue
        </h4>
        {s.queue.length === 0 && <p className="text-[11px] text-muted-foreground">Nothing else queued.</p>}
        {s.queue.map(({ ticket, idx }) => <div key={ticket.id}>{renderTicketRow(ticket, idx)}</div>)}
      </section>
    </div>
  );
}

/** DOM order of the sections (for the single SortableContext's items). */
export function multiBaySortableIds<T extends { id: string }>(techTickets: readonly T[], activity: MultiBayActivity): string[] {
  const s = partitionMultiBaySections(techTickets, activity);
  return [...s.handsOn, ...s.unattended, ...s.pausedInBay, ...s.queue].map((r) => r.ticket.id);
}

export default MultiBayTechnicianSections;
